import {
  useGlobalComponentForAttribute,
  useOverrides
} from '@rhino-project/core/hooks';
import { useModelContext } from '@rhino-project/core/hooks';
import { RhinoResource } from '@rhino-project/core';
import {
  getModelAndAttributeFromPath,
  getModelFromRef,
  isOwnerGlobal
} from '@rhino-project/core/utils';
import {
  ModelFieldBoolean,
  ModelFieldCurrency,
  ModelFieldCountry,
  ModelFieldDate,
  ModelFieldDateTime,
  ModelFieldEnum,
  ModelFieldFile,
  ModelFieldFloat,
  ModelFieldInteger,
  ModelFieldIntegerSelect,
  ModelFieldOwnerReference,
  ModelFieldPhone,
  ModelFieldReference,
  ModelFieldString,
  ModelFieldText,
  ModelFieldTime,
  ModelFieldYear
} from '../../ModelField';

const defaultComponents = {
  ModelFieldBoolean,
  ModelFieldCountry,
  ModelFieldCurrency,
  ModelFieldDate,
  ModelFieldDateTime,
  ModelFieldEnum,
  ModelFieldFile,
  ModelFieldFloat,
  ModelFieldInteger,
  ModelFieldIntegerSelect,
  ModelFieldOwnerReference,
  ModelFieldPhone,
  ModelFieldReference,
  ModelFieldString,
  ModelFieldText,
  ModelFieldTime,
  ModelFieldYear
};

export interface ModelFieldGroupProps {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  [key: string]: unknown;
}

export const ModelFieldGroupBase = ({
  overrides,
  ...originalProps
}: ModelFieldGroupProps) => {
  const {
    ModelFieldBoolean,
    ModelFieldCountry,
    ModelFieldCurrency,
    ModelFieldDate,
    ModelFieldDateTime,
    ModelFieldEnum,
    ModelFieldFile,
    ModelFieldFloat,
    ModelFieldInteger,
    ModelFieldIntegerSelect,
    // ModelFieldGroupJoinSimple,
    ModelFieldOwnerReference,
    ModelFieldPhone,
    ModelFieldReference,
    ModelFieldString,
    ModelFieldText,
    ModelFieldTime,
    ModelFieldYear
    // eslint-disable-next-line @eslint-react/static-components
  } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );
  const { model } = useModelContext();
  const { path } = originalProps;
  // FIXME: This should be solved in a better way with a FieldGroup context
  const props = { model, ...originalProps };

  const [, attribute] = getModelAndAttributeFromPath(
    model as RhinoResource,
    path
  );

  switch (attribute!.type) {
    case 'array': {
      // items is a heterogeneous OpenAPI schema (SchemaObject | ReferenceObject);
      // the dispatch inspects its dynamic anyOf shape.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items = attribute!.items as any;
      if (items?.anyOf?.[0]?.['$ref']?.endsWith('_attachment')) {
        // eslint-disable-next-line @eslint-react/static-components
        return <ModelFieldFile multiple {...props} />;
      }

      break;
    }
    case 'reference': {
      const refModel = getModelFromRef(attribute!);

      // eslint-disable-next-line @eslint-react/static-components
      if (path.endsWith('_attachment')) return <ModelFieldFile {...props} />;

      if (isOwnerGlobal(refModel as RhinoResource))
        // eslint-disable-next-line @eslint-react/static-components
        return <ModelFieldReference {...props} />;

      // eslint-disable-next-line @eslint-react/static-components
      return <ModelFieldOwnerReference {...props} />;
    }

    case 'boolean':
      // eslint-disable-next-line @eslint-react/static-components
      return <ModelFieldBoolean {...props} />;
    case 'integer':
      switch (attribute!.format) {
        case 'currency':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldCurrency step={1} {...props} />;
        case 'year':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldYear {...props} />;
        case 'select':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldIntegerSelect {...props} />;
        default:
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldInteger {...props} />;
      }
    case 'decimal':
    case 'number':
      if (attribute!.format === 'currency')
        // eslint-disable-next-line @eslint-react/static-components
        return <ModelFieldCurrency {...props} />;

      // eslint-disable-next-line @eslint-react/static-components
      return <ModelFieldFloat {...props} />;
    case 'text':
      // eslint-disable-next-line @eslint-react/static-components
      return <ModelFieldText {...props} />;
    case 'string':
      if (attribute!.enum) {
        // eslint-disable-next-line @eslint-react/static-components
        return <ModelFieldEnum {...props} />;
      }

      switch (attribute!.format) {
        case 'date':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldDate {...props} />;
        case 'datetime':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldDateTime {...props} />;
        case 'time':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldTime {...props} />;
        case 'phone':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldPhone {...props} />;
        case 'country':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldCountry {...props} />;
        default:
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelFieldString {...props} />;
      }

    default:
      console.assert(false, 'No available field for ', attribute);
  }

  return (
    <div>
      No field for attribute type &apos;{attribute!.type}&apos; and format
      &apos;
      {attribute!.format}&apos; - name &apos;{attribute!.name}&apos;
    </div>
  );
};

export const ModelFieldGroup = (props: ModelFieldGroupProps) =>
  useGlobalComponentForAttribute('ModelFieldGroup', ModelFieldGroupBase, props);
